import useSWR from 'swr';
import {useIntl} from 'react-intl';
import React, {useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router';

import {PostProcessingForm} from './PostProcessingForm';
import {toast} from '../../../base-components/StudioToast';
import {ModelPurpose} from '../../../types/model/ModelPurpose';
import {updatePostProcessor} from '../../../api/postprocessor/UpdatePostProcessor';
import {getPostProcessorFile} from '../../../api/postprocessor/ViewPostProcessorFile';
import {UpdatePostProcessorRequest} from '../../../types/postprocessor/UpdatePostProcessorRequest';
import {
  getPostProcessor,
  getPostProcessorURL,
} from '../../../api/postprocessor/GetPostProcessor';

type ReturnToOptions = 'spaCreate' | 'spaDetails' | undefined;

export const EditPostProcessor = () => {
  const location = useLocation();
  const search = useMemo(() => new URLSearchParams(location.search), [location]);
  const [files, setFiles] = useState<Record<string, string>>({});
  const returnTo = search.get('returnTo') as ReturnToOptions;
  const viewOnly = search.has('viewOnly');
  const applicationId = search.get('applicationId');
  const spaId = search.get('spaId');
  const deploymentId = search.get('deploymentId');
  const modelPurpose = search.has('modelPurpose')
    ? (search.get('modelPurpose') as ModelPurpose)
    : undefined;
  const {projectId, postProcessorId} = useParams<{
    projectId: string;
    postProcessorId: string;
  }>();
  const getPostProcessorKey = getPostProcessorURL(projectId, postProcessorId);
  const {data: getPostProcessorResult} = useSWR(getPostProcessorKey, getPostProcessor, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const template = useMemo(
    () =>
      getPostProcessorResult?.body
        ? {
            ...getPostProcessorResult?.body,
            modelPurpose: modelPurpose as ModelPurpose,
          }
        : undefined,
    [getPostProcessorResult, modelPurpose]
  );

  useEffect(() => {
    getFiles();

    async function getFiles() {
      const filePaths = getPostProcessorResult?.body?.filePaths ?? [];
      const allFiles: Record<string, string> = {};

      for (const fileName of filePaths) {
        const postProcessorType = getPostProcessorResult?.body?.postProcessorType;
        const executionContainer = getPostProcessorResult?.body?.executionContainer;
        const viewScriptParams = {
          projectId,
          id: postProcessorId,
          fileName,
          postProcessorType,
          executionContainer,
        };

        if (!viewScriptParams) {
          continue;
        }

        const {data: viewPostProcessorFileResult} = await getPostProcessorFile(
          viewScriptParams
        );
        const text = await viewPostProcessorFileResult.text();

        allFiles[fileName] = text;
      }

      setFiles(allFiles);
    }
  }, [getPostProcessorResult, projectId, postProcessorId]);

  const intl = useIntl();
  const history = useHistory();

  const returnToPreviousPage = () => {
    if (returnTo === 'spaCreate' && spaId) {
      if (deploymentId) {
        history.push(
          `/project/${projectId}/inference/${applicationId}/deployment/${deploymentId}/configure?spaId=${spaId}`
        );
      } else {
        history.push(
          `/project/${projectId}/inference/${applicationId}/create?spaId=${spaId}`
        );
      }
    } else if (returnTo === 'spaDetails' && applicationId) {
      history.push(
        `/spa/project/${projectId}/application/${applicationId}/inference/${spaId}/details`
      );
    } else {
      history.push(`/project/${projectId}/upload`);
    }
  };

  const handleSubmit = async (values: UpdatePostProcessorRequest) => {
    try {
      const resp = await updatePostProcessor(values);
      if (!resp.errors?.length) {
        toast.success(intl.formatMessage({id: 'editPostProcessor.success'}));
        returnToPreviousPage();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getReturnText = () => {
    switch (returnTo) {
      case 'spaCreate':
        return intl.formatMessage({id: 'form.returnToConfigure'});
      case 'spaDetails':
        return intl.formatMessage({id: 'form.returnToDetails'});
      default:
        return undefined;
    }
  };

  return (
    <PostProcessingForm
      title={
        viewOnly
          ? intl.formatMessage({id: 'viewPostProcessor.header'})
          : intl.formatMessage({id: 'editPostProcessor.header'})
      }
      returnText={getReturnText()}
      viewOnly={viewOnly}
      projectId={projectId}
      template={template}
      isInitializingData={!getPostProcessorResult?.body}
      filePaths={files}
      onSubmit={handleSubmit}
      onCancel={returnToPreviousPage}
    />
  );
};
